import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRestApi } from '../../useClient';
import { unitTypesApi, containerTypesApi, cargowiseOrganizationsApi, cargowiseHandlingMethodsApi, customFieldMappingsApi, integrationSettingsApi, integrationsApi, } from '../organizationSettings';
// Unit Types Hooks
export function useUnitTypes(integrationId, category) {
    const api = useRestApi();
    return useQuery({
        queryKey: ['unitTypes', integrationId, category],
        queryFn: () => {
            if (!integrationId)
                throw new Error('Integration ID is required');
            return unitTypesApi.getUnitTypes(api, integrationId, category);
        },
        enabled: !!integrationId,
    });
}
export function useCreateUnitType(integrationId) {
    const api = useRestApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => {
            if (!integrationId)
                throw new Error('Integration ID is required');
            return unitTypesApi.createUnitType(api, integrationId, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['unitTypes', integrationId] });
        },
    });
}
export function useUpdateUnitType(integrationId) {
    const api = useRestApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => {
            if (!integrationId)
                throw new Error('Integration ID is required');
            return unitTypesApi.updateUnitType(api, integrationId, id, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['unitTypes', integrationId] });
        },
    });
}
export function useDeleteUnitType(integrationId) {
    const api = useRestApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => {
            if (!integrationId)
                throw new Error('Integration ID is required');
            return unitTypesApi.deleteUnitType(api, integrationId, id);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['unitTypes', integrationId] });
        },
    });
}
// Container Types Hooks
export function useContainerTypes(integrationId) {
    const api = useRestApi();
    return useQuery({
        queryKey: ['containerTypes', integrationId],
        queryFn: () => {
            if (!integrationId)
                throw new Error('Integration ID is required');
            return containerTypesApi.getContainerTypes(api, integrationId);
        },
        enabled: !!integrationId,
    });
}
export function useCreateContainerType(integrationId) {
    const api = useRestApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => {
            if (!integrationId)
                throw new Error('Integration ID is required');
            return containerTypesApi.createContainerType(api, integrationId, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['containerTypes', integrationId] });
        },
    });
}
export function useUpdateContainerType(integrationId) {
    const api = useRestApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ woveCode, data }) => {
            if (!integrationId)
                throw new Error('Integration ID is required');
            return containerTypesApi.updateContainerType(api, integrationId, woveCode, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['containerTypes', integrationId] });
        },
    });
}
export function useDeleteContainerType(integrationId) {
    const api = useRestApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (woveCode) => {
            if (!integrationId)
                throw new Error('Integration ID is required');
            return containerTypesApi.deleteContainerType(api, integrationId, woveCode);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['containerTypes', integrationId] });
        },
    });
}
// CargoWise Organizations Hooks
export function useCargowiseOrganizations(activeOnly) {
    const api = useRestApi();
    return useQuery({
        queryKey: ['cargowiseOrganizations', activeOnly],
        queryFn: () => cargowiseOrganizationsApi.getOrganizations(api, activeOnly),
    });
}
export function useCargowiseOrganization(organizationId) {
    const api = useRestApi();
    return useQuery({
        queryKey: ['cargowiseOrganization', organizationId],
        queryFn: () => {
            if (!organizationId)
                throw new Error('Organization ID is required');
            return cargowiseOrganizationsApi.getOrganization(api, organizationId);
        },
        enabled: !!organizationId,
    });
}
export function useCreateCargowiseOrganization() {
    const api = useRestApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => cargowiseOrganizationsApi.createOrganization(api, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cargowiseOrganizations'] });
        },
    });
}
export function useUpdateCargowiseOrganization() {
    const api = useRestApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ organizationId, data }) => cargowiseOrganizationsApi.updateOrganization(api, organizationId, data),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['cargowiseOrganizations'] });
            queryClient.invalidateQueries({ queryKey: ['cargowiseOrganization', variables.organizationId] });
        },
    });
}
export function useDeleteCargowiseOrganization() {
    const api = useRestApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (organizationId) => cargowiseOrganizationsApi.deleteOrganization(api, organizationId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cargowiseOrganizations'] });
        },
    });
}
export function useSearchCargowiseOrganizations() {
    const api = useRestApi();
    return useMutation({
        mutationFn: (query) => cargowiseOrganizationsApi.searchOrganizations(api, query),
    });
}
// CargoWise Handling Methods Hooks
export function useCargowiseHandlingMethods(activeOnly) {
    const api = useRestApi();
    return useQuery({
        queryKey: ['cargowiseHandlingMethods', activeOnly],
        queryFn: () => cargowiseHandlingMethodsApi.getHandlingMethods(api, activeOnly),
    });
}
export function useCargowiseHandlingMethod(handlingMethodId) {
    const api = useRestApi();
    return useQuery({
        queryKey: ['cargowiseHandlingMethod', handlingMethodId],
        queryFn: () => {
            if (!handlingMethodId)
                throw new Error('Handling method ID is required');
            return cargowiseHandlingMethodsApi.getHandlingMethod(api, handlingMethodId);
        },
        enabled: !!handlingMethodId,
    });
}
export function useCreateCargowiseHandlingMethod() {
    const api = useRestApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => cargowiseHandlingMethodsApi.createHandlingMethod(api, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cargowiseHandlingMethods'] });
        },
    });
}
export function useUpdateCargowiseHandlingMethod() {
    const api = useRestApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ handlingMethodId, data }) => cargowiseHandlingMethodsApi.updateHandlingMethod(api, handlingMethodId, data),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['cargowiseHandlingMethods'] });
            queryClient.invalidateQueries({ queryKey: ['cargowiseHandlingMethod', variables.handlingMethodId] });
        },
    });
}
export function useDeleteCargowiseHandlingMethod() {
    const api = useRestApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (handlingMethodId) => cargowiseHandlingMethodsApi.deleteHandlingMethod(api, handlingMethodId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cargowiseHandlingMethods'] });
        },
    });
}
export function useSearchCargowiseHandlingMethods() {
    const api = useRestApi();
    return useMutation({
        mutationFn: (query) => cargowiseHandlingMethodsApi.searchHandlingMethods(api, query),
    });
}
// Custom Field Mappings Hooks
export function useCustomFieldMappings(integrationId) {
    const api = useRestApi();
    return useQuery({
        queryKey: ['customFieldMappings', integrationId],
        queryFn: () => {
            if (!integrationId)
                throw new Error('Integration ID is required');
            return customFieldMappingsApi.getCustomFieldMappings(api, integrationId);
        },
        enabled: !!integrationId,
    });
}
export function useCreateCustomFieldMapping(integrationId) {
    const api = useRestApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => {
            if (!integrationId)
                throw new Error('Integration ID is required');
            return customFieldMappingsApi.createCustomFieldMapping(api, integrationId, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['customFieldMappings', integrationId] });
        },
    });
}
export function useUpdateCustomFieldMapping(integrationId) {
    const api = useRestApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => {
            if (!integrationId)
                throw new Error('Integration ID is required');
            return customFieldMappingsApi.updateCustomFieldMapping(api, integrationId, id, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['customFieldMappings', integrationId] });
        },
    });
}
export function useDeleteCustomFieldMapping(integrationId) {
    const api = useRestApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => {
            if (!integrationId)
                throw new Error('Integration ID is required');
            return customFieldMappingsApi.deleteCustomFieldMapping(api, integrationId, id);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['customFieldMappings', integrationId] });
        },
    });
}
// Integration Settings Hooks
export function useIntegrationSettings(integrationId) {
    const api = useRestApi();
    return useQuery({
        queryKey: ['integrationSettings', integrationId],
        queryFn: () => {
            if (!integrationId)
                throw new Error('Integration ID is required');
            return integrationSettingsApi.getIntegrationSettings(api, integrationId);
        },
        enabled: !!integrationId,
    });
}
export function useUpdateIntegrationSettings(integrationId) {
    const api = useRestApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => {
            if (!integrationId)
                throw new Error('Integration ID is required');
            return integrationSettingsApi.updateIntegrationSettings(api, integrationId, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['integrationSettings', integrationId] });
            queryClient.invalidateQueries({ queryKey: ['cargoWiseSettings', integrationId] });
        },
    });
}
export function useCargoWiseSettings(integrationId) {
    const api = useRestApi();
    return useQuery({
        queryKey: ['cargoWiseSettings', integrationId],
        queryFn: () => {
            if (!integrationId)
                throw new Error('Integration ID is required');
            return integrationSettingsApi.getCargoWiseSettings(api, integrationId);
        },
        enabled: !!integrationId,
    });
}
export function useUpdateCargoWiseSettings(integrationId) {
    const api = useRestApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => {
            if (!integrationId)
                throw new Error('Integration ID is required');
            return integrationSettingsApi.updateCargoWiseSettings(api, integrationId, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cargoWiseSettings', integrationId] });
            queryClient.invalidateQueries({ queryKey: ['integrationSettings', integrationId] });
        },
    });
}
export function useTestCargoWiseConnection(integrationId) {
    const api = useRestApi();
    return useMutation({
        mutationFn: (credentials) => {
            if (!integrationId)
                throw new Error('Integration ID is required');
            return integrationSettingsApi.testCargoWiseConnection(api, integrationId, credentials?.credentials);
        },
    });
}
// Integrations REST API Hooks (alternative to GraphQL)
export function useRestIntegrations() {
    const api = useRestApi();
    return useQuery({
        queryKey: ['restIntegrations'],
        queryFn: () => integrationsApi.getIntegrations(api),
    });
}
export function useCreateIntegration() {
    const api = useRestApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => integrationsApi.createIntegration(api, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['restIntegrations'] });
            queryClient.invalidateQueries({ queryKey: ['integrations'] }); // Also invalidate GraphQL cache
        },
    });
}
export function useRestIntegration(integrationId) {
    const api = useRestApi();
    return useQuery({
        queryKey: ['restIntegration', integrationId],
        queryFn: () => {
            if (!integrationId)
                throw new Error('Integration ID is required');
            return integrationsApi.getIntegration(api, integrationId);
        },
        enabled: !!integrationId,
    });
}
export function useUpdateIntegration() {
    const api = useRestApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ integrationId, data }) => integrationsApi.updateIntegration(api, integrationId, data),
        onSuccess: (_, { integrationId }) => {
            queryClient.invalidateQueries({ queryKey: ['restIntegration', integrationId] });
            queryClient.invalidateQueries({ queryKey: ['restIntegrations'] });
            queryClient.invalidateQueries({ queryKey: ['integrations'] }); // Also invalidate GraphQL cache
        },
    });
}
export function useDeleteIntegration() {
    const api = useRestApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (integrationId) => integrationsApi.deleteIntegration(api, integrationId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['restIntegrations'] });
            queryClient.invalidateQueries({ queryKey: ['integrations'] }); // Also invalidate GraphQL cache
        },
    });
}
